/**
 * Snippet Library
 * Full CRUD management for reusable content blocks used in AI draft generation.
 */

import { useState, useEffect } from 'react';
import { env } from '@/lib/env';
import { getAuthItem } from '@/lib/authStorage';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';

interface Snippet {
  id: string;
  workspaceId: string;
  label: string;
  shortcut?: string;
  text: string;
  stages: string[];
  category?: string;
  createdAt: string;
  updatedAt: string;
}

interface SnippetLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  stages?: string[];
}

const DEFAULT_SNIPPETS = [
  { label: 'Accredited Investor', shortcut: 'accredited', text: 'As an accredited investor, you would qualify for our fund offerings which require a minimum investment. I can walk you through the details and share our latest performance data.', stages: [], category: 'qualification' },
  { label: 'Fund Performance', shortcut: 'perf', text: 'Our fund has delivered consistent returns, and I would be happy to share our latest quarterly report and performance metrics with you.', stages: [], category: 'info' },
  { label: 'Calendar Booking', shortcut: 'cal', text: 'I would love to schedule a call to discuss this further. Would any of the following times work for you? Alternatively, feel free to pick a slot that works best for your schedule.', stages: [], category: 'action' },
  { label: 'Minimum Investment', shortcut: 'min', text: 'Our minimum investment threshold is designed to ensure meaningful participation. I can provide the specific details and discuss how we can structure this to meet your investment goals.', stages: [], category: 'info' },
  { label: 'Nurture Message', shortcut: 'nurture', text: 'I wanted to check in and see how things are going on your end. No rush on anything — just wanted to stay connected and let you know I am here if you have any questions.', stages: [], category: 'nurture' },
];

function getHeaders(): Record<string, string> {
  const token = getAuthItem('xavior_auth_token');
  const userId = getAuthItem('userId');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(userId ? { 'X-User-Id': userId } : {}),
  };
}

export function SnippetLibrary({ isOpen, onClose, stages }: SnippetLibraryProps) {
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingSnippet, setEditingSnippet] = useState<Partial<Snippet> | null>(null);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) loadSnippets();
  }, [isOpen]);

  async function loadSnippets() {
    setLoading(true);
    try {
      const res = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/snippets`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSnippets(data.snippets || []);
      }
    } catch (err) {
      console.error('Failed to load snippets:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    if (!editingSnippet || !editingSnippet.label || !editingSnippet.text) return;
    setSaving(true);
    try {
      const isEdit = editingSnippet.id;
      const url = isEdit
        ? `${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/snippets/${editingSnippet.id}`
        : `${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/snippets`;
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getHeaders(),
        body: JSON.stringify({
          label: editingSnippet.label,
          shortcut: editingSnippet.shortcut || undefined,
          text: editingSnippet.text,
          stages: editingSnippet.stages || [],
          category: editingSnippet.category || undefined,
        }),
      });

      if (res.ok) {
        setEditingSnippet(null);
        await loadSnippets();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Failed to save snippet');
      }
    } catch (err) {
      console.error('Failed to save snippet:', err);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this snippet?')) return;
    try {
      const res = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/snippets/${id}`, {
        method: 'DELETE',
        headers: getHeaders(),
      });
      if (res.ok) {
        setSnippets(s => s.filter(x => x.id !== id));
      }
    } catch (err) {
      console.error('Failed to delete snippet:', err);
    }
  }

  async function handleSeedDefaults() {
    setSaving(true);
    try {
      for (const s of DEFAULT_SNIPPETS) {
        await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/snippets`, {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(s),
        });
      }
      await loadSnippets();
    } catch (err) {
      console.error('Failed to seed snippets:', err);
    } finally {
      setSaving(false);
    }
  }

  const filtered = snippets.filter(s =>
    !search || s.label.toLowerCase().includes(search.toLowerCase()) ||
    (s.shortcut && s.shortcut.toLowerCase().includes(search.toLowerCase()))
  );

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'var(--color-surface)', borderRadius: 12, width: '90%', maxWidth: 640,
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #eee',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Snippet Library</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--color-text-faint)' }}>
            &times;
          </button>
        </div>

        {/* Search + Actions */}
        <div style={{ padding: '12px 20px', display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search snippets..."
            style={{ flex: 1, padding: '6px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, outline: 'none' }}
          />
          <button
            onClick={() => setEditingSnippet({ label: '', shortcut: '', text: '', stages: [], category: '' })}
            style={{ padding: '6px 12px', background: 'var(--color-brand)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, cursor: 'pointer' }}
          >
            + New
          </button>
        </div>

        {/* List */}
        <div className="xv-scroll" style={{ flex: 1, overflowY: 'auto', padding: '0 20px 16px' }}>
          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-faint)' }}>Loading...</div>
          ) : snippets.length === 0 ? (
            <div style={{ padding: 20, textAlign: 'center' }}>
              <p style={{ color: 'var(--color-text-faint)', marginBottom: 12 }}>No snippets yet.</p>
              <button
                onClick={handleSeedDefaults}
                disabled={saving}
                style={{ padding: '8px 16px', background: 'var(--color-brand)', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}
              >
                {saving ? 'Seeding...' : 'Seed Default Snippets'}
              </button>
            </div>
          ) : (
            filtered.map(snippet => (
              <div key={snippet.id} style={{
                padding: '10px 12px', borderBottom: '1px solid #f0f0f0',
                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8,
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{snippet.label}</span>
                    {snippet.shortcut && (
                      <span style={{ fontSize: 11, color: 'var(--color-brand)', background: 'var(--xavior-blue-bg, #e8f0fe)', padding: '1px 6px', borderRadius: 4 }}>
                        /{snippet.shortcut}
                      </span>
                    )}
                    {snippet.category && (
                      <span style={{ fontSize: 10, color: '#666', background: '#f0f0f0', padding: '1px 6px', borderRadius: 4 }}>
                        {snippet.category}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {snippet.text}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  <button
                    onClick={() => setEditingSnippet({ ...snippet })}
                    style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11 }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(snippet.id)}
                    style={{ background: 'none', border: '1px solid #fca5a5', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 11, color: '#dc2626' }}
                  >
                    Del
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Edit form */}
        {editingSnippet && (
          <div style={{
            padding: '16px 20px', borderTop: '1px solid #eee', background: '#fafafa',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                value={editingSnippet.label || ''}
                onChange={(e) => setEditingSnippet({ ...editingSnippet, label: e.target.value })}
                placeholder="Label"
                style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, outline: 'none' }}
              />
              <input
                type="text"
                value={editingSnippet.shortcut || ''}
                onChange={(e) => setEditingSnippet({ ...editingSnippet, shortcut: e.target.value })}
                placeholder="Shortcut (e.g. perf)"
                style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, outline: 'none' }}
              />
            </div>
            <textarea
              value={editingSnippet.text || ''}
              onChange={(e) => setEditingSnippet({ ...editingSnippet, text: e.target.value })}
              placeholder="Snippet text..."
              rows={3}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, resize: 'vertical', outline: 'none', marginBottom: 8, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingSnippet(null)}
                style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 6, background: 'var(--color-surface)', cursor: 'pointer', fontSize: 13 }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editingSnippet.label || !editingSnippet.text}
                style={{
                  padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13,
                  background: (!editingSnippet.label || !editingSnippet.text) ? '#ccc' : 'var(--color-brand)', color: 'white',
                }}
              >
                {saving ? 'Saving...' : editingSnippet.id ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
